import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Tabs, Tab, Form, Button, Alert } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import axios from '../utils/axios';
import { useAuth } from '../utils/AuthContext';
import Subscription from '../components/Subscription';
import SlackIntegration from '../components/SlackIntegration';

const Profile = () => {
  const { currentUser, updateUser } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  // Handle tab selection from navigation state
  useEffect(() => {
    if (location.state && location.state.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location]);
  
  const [userPlan, setUserPlan] = useState({
    type: 'free',
    status: 'trial',
    trialEndsAt: null,
    usageSummary: {
      meetingsProcessed: 0,
      minutesProcessed: 0
    }
  });
  
  useEffect(() => {
    if (currentUser) {
      setUserData({
        ...userData,
        name: currentUser.name || '',
        email: currentUser.email || ''
      });
      
      fetchUserPlanDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);
  
  const fetchUserPlanDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/user/plan');
      
      if (response.data.success) {
        setUserPlan(response.data.plan);
      }
    } catch (err) {
      console.error('Error fetching plan details:', err);
      // Fallback to mock data in development
      setUserPlan({
        type: currentUser?.plan || 'free',
        status: currentUser?.paymentStatus || 'trial',
        trialEndsAt: currentUser?.trialEndsAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        usageSummary: {
          meetingsProcessed: currentUser?.usageThisMonth?.meetingsProcessed || 0,
          minutesProcessed: currentUser?.usageThisMonth?.minutesProcessed || 0
        }
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value
    });
  };
  
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validate password match if changing password
    if (userData.password && userData.password !== userData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    try {
      setLoading(true);
      
      // Only send password if it's being changed
      const dataToUpdate = {
        name: userData.name,
        email: userData.email
      };
      
      if (userData.password) {
        dataToUpdate.password = userData.password;
      }
      
      const response = await axios.put('/api/user/profile', dataToUpdate);
      
      if (response.data.success) {
        setSuccess('Profile updated successfully');
        updateUser(response.data.user);
        
        // Clear password fields after update
        setUserData({
          ...userData,
          password: '',
          confirmPassword: ''
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container className="mt-4">
      <h2 className="mb-4">Your Profile</h2>
      
      <Tabs 
        activeKey={activeTab} 
        onSelect={(k) => setActiveTab(k)} 
        className="mb-4"
      >
        <Tab eventKey="profile" title="User Details">
          <Card>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              
              <Form onSubmit={handleProfileUpdate}>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm={3}>Name</Form.Label>
                  <Col sm={9}>
                    <Form.Control
                      type="text"
                      name="name"
                      value={userData.name}
                      onChange={handleChange}
                      required
                    />
                  </Col>
                </Form.Group>
                
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm={3}>Email</Form.Label>
                  <Col sm={9}>
                    <Form.Control
                      type="email"
                      name="email"
                      value={userData.email}
                      onChange={handleChange}
                      required
                    />
                  </Col>
                </Form.Group>
                
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm={3}>New Password</Form.Label>
                  <Col sm={9}>
                    <Form.Control
                      type="password"
                      name="password"
                      value={userData.password}
                      onChange={handleChange}
                      placeholder="Leave blank to keep current password"
                    />
                  </Col>
                </Form.Group>
                
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm={3}>Confirm Password</Form.Label>
                  <Col sm={9}>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      value={userData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Leave blank to keep current password"
                    />
                  </Col>
                </Form.Group>
                
                <div className="text-end">
                  <Button 
                    type="submit" 
                    variant="primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Profile'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="subscription" title="Subscription">
          <Card className="mb-4">
            <Card.Body>
              <h5>Current Plan: <span className="text-primary">{userPlan.type.charAt(0).toUpperCase() + userPlan.type.slice(1)}</span></h5>
              <p>Status: <span className={`badge bg-${userPlan.status === 'active' ? 'success' : userPlan.status === 'trial' ? 'warning' : 'danger'}`}>
                {userPlan.status === 'active' ? 'Active' : userPlan.status === 'trial' ? 'Trial' : 'Inactive'}
              </span></p>
              
              {userPlan.status === 'trial' && userPlan.trialEndsAt && (
                <p>Trial ends on: {new Date(userPlan.trialEndsAt).toLocaleDateString()}</p>
              )}
              
              <h6 className="mt-3">Usage This Month:</h6>
              <ul>
                <li>Meetings Processed: {userPlan.usageSummary.meetingsProcessed}</li>
                <li>Minutes Processed: {userPlan.usageSummary.minutesProcessed}</li>
              </ul>
            </Card.Body>
          </Card>
          
          <Subscription />
        </Tab>
        
        <Tab eventKey="integrations" title="Integrations">
          <SlackIntegration />
          
          {/* Placeholder for future integrations */}
          <Card className="mb-3 opacity-50">
            <Card.Body className="d-flex align-items-center">
              <img 
                src="https://cdn.worldvectorlogo.com/logos/hubspot.svg" 
                alt="HubSpot" 
                style={{ width: 30, height: 30, marginRight: 12 }}
              />
              <div>
                <strong>HubSpot</strong>
                <p className="mb-0 text-muted small">Coming soon - Log meeting notes to your CRM</p>
              </div>
            </Card.Body>
          </Card>
          
          <Card className="mb-3 opacity-50">
            <Card.Body className="d-flex align-items-center">
              <img 
                src="https://cdn.worldvectorlogo.com/logos/salesforce-2.svg" 
                alt="Salesforce" 
                style={{ width: 30, height: 30, marginRight: 12 }}
              />
              <div>
                <strong>Salesforce</strong>
                <p className="mb-0 text-muted small">Coming soon - Sync meetings with opportunities</p>
              </div>
            </Card.Body>
          </Card>
          
          <Card className="mb-3 opacity-50">
            <Card.Body className="d-flex align-items-center">
              <img 
                src="https://cdn.worldvectorlogo.com/logos/asana-logo.svg" 
                alt="Asana" 
                style={{ width: 30, height: 30, marginRight: 12 }}
              />
              <div>
                <strong>Asana</strong>
                <p className="mb-0 text-muted small">Coming soon - Auto-create tasks from action items</p>
              </div>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default Profile;
